import { useMemo } from 'react'

import { useAuth } from '../auth'
import { formatHM, formatHMS, formatHMShort, formatMoneyShort } from '../lib/format'
import { local, queueOp } from '../lib/sync'
import type { Task } from '../types'
import { IconChevron, IconPause, IconPlay, IconPlus, IconStop } from './icons'
import { useLocal, useTicker } from './useLocal'

const DAYS = ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота']
const MONTHS = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
]

/** Имена в справочнике записаны как «Фамилия И.О.», поэтому берём первые
 *  буквы первых двух частей: «Петрова С.А.» — «ПС». */
function initials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/)
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase()
}

function todayISO(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export default function Today({ onOpenTask, onNewTask, onAllTasks, onProfile }: {
  onOpenTask: (id: number) => void
  onNewTask: () => void
  onAllTasks: () => void
  onProfile: () => void
}) {
  const { user } = useAuth()
  const { rows: tasks } = useLocal<Task>(local.tasks)

  const running = useMemo(() => tasks.find((t) => t.running_since) ?? null, [tasks])
  useTicker(!!running)

  const today = todayISO()
  const todays = useMemo(
    () => tasks.filter((t) => t.work_date === today).slice(0, 6),
    [tasks, today],
  )

  const stats = useMemo(() => {
    const secondsToday = tasks
      .filter((t) => t.work_date === today)
      .reduce((s, t) => s + t.tracked_seconds, 0)
    const secondsMonth = tasks.reduce((s, t) => s + t.tracked_seconds, 0)
    const toPay = tasks
      .filter((t) => t.payment_status === 'to_pay')
      .reduce((s, t) => s + Number(t.amount ?? 0), 0)
    return { secondsToday, secondsMonth, toPay }
  }, [tasks, today])

  const runningSeconds = running?.running_since
    ? running.tracked_seconds +
      Math.max(0, Math.floor((Date.now() - new Date(running.running_since).getTime()) / 1000))
    : 0

  const now = new Date()

  return (
    <>
      <div className="m-head">
        <div className="m-head-row">
          <div>
            <div className="m-head-date">
              {DAYS[now.getDay()][0].toUpperCase() + DAYS[now.getDay()].slice(1)},{' '}
              {now.getDate()} {MONTHS[now.getMonth()]}
            </div>
            <div className="m-head-name">{user?.full_name}</div>
          </div>
          <button className="m-avatar" onClick={onProfile} aria-label="Профиль">
            {initials(user?.full_name ?? '')}
          </button>
        </div>
      </div>

      {running ? (
        <div className="m-timer-card">
          <div className="m-timer-stripe" />
          <div className="m-timer-body">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <span className="m-dot" />
                <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--m-green)' }}>
                  Идёт работа
                </span>
              </div>
              <div style={{ fontSize: 13, color: 'var(--m-muted)' }}>
                с {new Date(running.running_since!).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>

            <button
              onClick={() => onOpenTask(running.id)}
              style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', font: 'inherit', cursor: 'pointer' }}
            >
              <div className="m-timer-value">{formatHMS(runningSeconds)}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--m-navy)', marginTop: 4 }}>
                {running.client_name}
              </div>
              <div style={{ fontSize: 14, color: 'var(--m-muted)', marginTop: 4 }}>{running.title}</div>
            </button>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                className="m-btn m-btn-pause"
                style={{ flexGrow: 1 }}
                onClick={() => queueOp('timer.stop', { taskId: running.id, finish: false })}
              >
                <IconPause color="#a15c07" />
                Пауза
              </button>
              <button
                className="m-btn m-btn-stop"
                style={{ flexGrow: 1 }}
                onClick={() => queueOp('timer.stop', { taskId: running.id, finish: true })}
              >
                <IconStop />
                Завершить
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="m-timer-card">
          <div className="m-timer-body" style={{ gap: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--m-muted)' }}>
              Таймер
            </div>
            <div className="m-timer-value" style={{ color: 'var(--m-faint)' }}>0:00:00</div>
            <div style={{ fontSize: 14, color: 'var(--m-muted)' }}>
              Не запущен. Начните задачу из списка ниже.
            </div>
          </div>
        </div>
      )}

      <div className="m-kpis">
        <div className="m-kpi">
          <div className="m-kpi-label">Сегодня</div>
          <div className="m-kpi-value">{formatHMShort(stats.secondsToday)}</div>
        </div>
        <div className="m-kpi">
          <div className="m-kpi-label">За месяц</div>
          <div className="m-kpi-value">{formatHMShort(stats.secondsMonth)}</div>
        </div>
        <div className="m-kpi m-kpi-money">
          <div className="m-kpi-label">К выплате</div>
          <div className="m-kpi-value">{formatMoneyShort(stats.toPay)}</div>
        </div>
      </div>

      <div className="m-section">
        <div className="m-section-title">Сегодня</div>
        <button className="m-link" onClick={onAllTasks}>Все задачи</button>
      </div>

      <div className="m-list">
        {todays.length === 0 && <div className="m-empty">На сегодня задач нет</div>}
        {todays.map((t) => (
          <TaskRow key={t.id} task={t} running={!!running} onOpen={() => onOpenTask(t.id)} />
        ))}
      </div>

      <button className="m-fab" onClick={onNewTask} aria-label="Новая задача">
        <IconPlus />
      </button>
    </>
  )
}

function TaskRow({ task, running, onOpen }: { task: Task; running: boolean; onOpen: () => void }) {
  const closed = task.payment_status === 'paid' || task.payment_status === 'in_payout'
  const done = task.work_status === 'done' || task.work_status === 'cancelled'
  const hasHistory = task.tracked_seconds > 0
  const canStart = !closed && !done && !task.running_since && !running

  return (
    <div className="m-card" style={{ display: 'flex', alignItems: 'center', gap: 12, opacity: done ? 0.72 : 1 }}>
      <button
        onClick={onOpen}
        style={{ flexGrow: 1, minWidth: 0, background: 'none', border: 'none', padding: 0, textAlign: 'left', font: 'inherit', cursor: 'pointer' }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
          <span className="m-sub">№ {task.id}</span>
          <span className="m-client">{task.client_name}</span>
        </div>
        <div style={{ fontSize: 14, marginTop: 3 }}>{task.title}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 5 }}>
          {task.estimate !== null ? (
            <span className="m-chip m-chip-est">{Number(task.estimate)} ч</span>
          ) : (
            <span className="m-chip m-chip-none">без оценки</span>
          )}
          {task.is_support && <span className="m-chip m-chip-support">сопровождение</span>}
          {done && <span className="m-chip m-chip-done">завершено</span>}
          {task.payment_status === 'in_payout' && <span className="m-chip m-chip-payout">в ведомости</span>}
          <span className="m-sub">{formatHM(task.tracked_seconds)}</span>
        </div>
      </button>

      {canStart ? (
        <button
          className={`m-round ${hasHistory ? 'm-round-resume' : 'm-round-go'}`}
          onClick={() => queueOp('timer.start', { taskId: task.id })}
          aria-label={hasHistory ? 'Продолжить' : 'Старт'}
        >
          <IconPlay color={hasHistory ? '#a15c07' : '#fff'} />
        </button>
      ) : (
        <IconChevron color="#94a3b8" />
      )}
    </div>
  )
}
